import { kebabCase } from 'lodash';
import { GetServerSideProps } from 'next';
import { useParams } from 'next/navigation';
import { useContext, useEffect, useState } from 'react';

import PacksList from '@/components/app/cards/PacksList';
import { Tags } from '@/components/app/cards/TagsList/types';
import { PacksContext } from '@/components/app/layout/PacksProvider';
import Layout from '@/components/layout/LayoutWrapper';
import { usePacksClass } from '@/hooks/app/usePacksClass';
import { usePacksFeed } from '@/hooks/app/usePacksFeed';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

import { customFeedNames } from '.';

const getTagId = (tags: Tags, tagName: string) => {
	return tags.find(tag => kebabCase(tag.title) === tagName);
};

const TagName = () => {
	const params = useParams();
	const routerPush = useRouterPush();
	const { setPageTitle } = useContext(PacksContext);

	const feedQueryParam = typeof params?.feedName === 'string' ? params.feedName : '';
	const tagName = typeof params?.tagName === 'string' ? params.tagName : '';

	const [packListType, setPackListType] = useState<'feed' | 'class'>('class');

	const feedName = customFeedNames[feedQueryParam] ?? feedQueryParam;

	const {
		tags,
		isLoading: isTagsLoading,
		title: feedTitle,
		error: tagsError,
		hasResponse: tagsHasResponse,
		notFound: tagsNotFound,
	} = usePacksFeed(feedName);
	const activeTag = getTagId(tags, tagName);

	const {
		packs: feedPacks,
		isLoading: isFeedLoading,
		error: feedError,
		hasResponse: feedHasResponse,
		notFound: feedNotFound,
	} = usePacksFeed(packListType === 'feed' && activeTag?.feed_id ? activeTag.feed_id : null);
	const {
		pack: packFromClass,
		isLoading: isClassLoading,
		error: classError,
		hasResponse: classHasResponse,
		notFound: classNotFound,
	} = usePacksClass(packListType === 'class' && activeTag?.pack_class ? activeTag.pack_class : null);

	useEffect(() => {
		if (activeTag?.feed_id) {
			// TODO: refactor this code to follow best practices
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setPackListType('feed');
		} else if (activeTag?.pack_class) {
			setPackListType('class');
		}
	}, [activeTag]);

	useEffect(() => {
		if (feedTitle) {
			setPageTitle(feedTitle);
		}
	}, [feedTitle, setPageTitle]);

	const getPacks = () => {
		if (packListType === 'feed') {
			return feedPacks;
		}

		if (packFromClass) {
			return [packFromClass];
		}

		return [];
	};

	const packs = getPacks();
	const isLoading = isTagsLoading || isFeedLoading || isClassLoading;

	// A tagName that matches no tag in a successfully-loaded tags list — or that matches a
	// tag with neither a feed_id nor a pack_class to look up — is itself a confirmed
	// absence: there's nothing for the feed/class hooks below to fetch, so packListType
	// stays at its default and their `hasResponse` can never become true from that dead
	// end. Without this short-circuit, `hasResponse` (and effectiveIsLoading below) would
	// stay permanently unresolved instead of ever reaching the redirect.
	const activeTagNotFound =
		tagsHasResponse && !tagsError && (!activeTag || (!activeTag.feed_id && !activeTag.pack_class));

	// A tags fetch error always blocks the redirect; a feed/class fetch error only matters
	// while we're actually relying on that source's packs. Only a source confirmed empty by
	// a response that actually arrived is a 404 — an empty `packs` on its own doesn't
	// distinguish that from a request that never landed, and search opens this route often
	// enough that a transient failure is no longer a rare edge case.
	const error = activeTagNotFound
		? undefined
		: (tagsError ?? (packListType === 'feed' ? feedError : classError));
	const hasResponse =
		activeTagNotFound || (tagsHasResponse && (packListType === 'feed' ? feedHasResponse : classHasResponse));
	// A 404 from the tags fetch means the collection itself doesn't exist, regardless of
	// packListType; a 404 from the feed/class fetch only confirms absence for the source
	// this tag is currently relying on. Either is a confirmed absence that should redirect
	// even though `error` (used above) would otherwise hold off out of caution.
	const notFound =
		activeTagNotFound || tagsNotFound || (packListType === 'feed' ? feedNotFound : classNotFound);

	// Two shapes leave `isLoading` false with nothing confirmed: a malformed 200 body (no
	// error, but nothing usable either), and a network-level failure (a rejected request
	// with no HTTP status at all — DNS, timeout, reset). Both are unconfirmed, so treat them
	// as still-loading everywhere `isLoading` gates what's shown, not just the redirect
	// below. A genuine HTTP error — a status is present, including a confirmed 404 — falls
	// through instead: retrying forever behind a loader wouldn't help there, and `notFound`
	// below already decides what happens with the 404 case specifically.
	const effectiveIsLoading = isLoading || (!hasResponse && (!error || error?.status === undefined));

	// The better solution here would be to figure out the lifecycle of the above
	// loading state to see why occasionally isLoading is false when it should be true
	// and thus prematurely redirecting but I have to move on with my life so I'm
	// using a timeout - MB
	useEffect(() => {
		const timeout = setTimeout(() => {
			if (!effectiveIsLoading && (notFound || (!error && packs.length <= 0))) {
				routerPush('/404');
			}
		}, 500);

		return () => clearTimeout(timeout);
	}, [effectiveIsLoading, packs, error, notFound, routerPush]);

	if (!effectiveIsLoading && (notFound || (!error && packs.length <= 0))) {
		return null;
	}

	return (
		<Layout pageName={feedName}>
			<PacksList
				packs={packs}
				tags={tags}
				activeTag={activeTag}
				packFeedName={feedName}
				isLoading={effectiveIsLoading}
			/>
		</Layout>
	);
};

export default TagName;

export const getServerSideProps: GetServerSideProps = async context => {
	const initialState = await getInitialServerState(context);

	return {
		props: {
			initialState: {
				...initialState,
				theme: {
					webAppLayout: 'full',
				},
			},
		},
	};
};
